import { useState } from "react";
import { Box, Paper, Stack, Button, Alert, Chip, TextField, MenuItem, CircularProgress } from "@mui/material";
import { PageHeader } from "@/components/PageHeader";
import AddIcon from "@mui/icons-material/Add";
import { DataGrid, type GridColDef } from "@mui/x-data-grid";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { diagnosticsApi, type DxReflexRuleDto, type DxResultFlag } from "@/api/diagnostics";
import { errorMessage } from "@/api/client";
import { useAuth } from "@/store/auth";

const FLAGS: DxResultFlag[] = ["Low", "High", "CriticalLow", "CriticalHigh", "Abnormal"];

export function DiagnosticsReflexRulesPage() {
  const qc = useQueryClient();
  const canManage = useAuth(s => s.hasPermission("diagnostics.catalog.manage"));
  const [trigger, setTrigger] = useState(""); const [flag, setFlag] = useState<DxResultFlag>("High"); const [reflex, setReflex] = useState("");

  const tests = useQuery({ queryKey: ["dx-tests-all"], queryFn: () => diagnosticsApi.listTests(undefined, true) });
  const testName = (id: string) => tests.data?.find(t => t.id === id)?.code ?? id.slice(0, 8);
  const rules = useQuery({ queryKey: ["dx-reflex"], queryFn: () => diagnosticsApi.listReflexRules() });

  const create = useMutation({
    meta: { successMessage: "Reflex rule created" },
    mutationFn: () => diagnosticsApi.createReflexRule({ triggerLabTestId: trigger, triggerFlag: flag, reflexLabTestId: reflex }),
    onSuccess: () => { setTrigger(""); setReflex(""); qc.invalidateQueries({ queryKey: ["dx-reflex"] }); },
  });

  const columns: GridColDef<DxReflexRuleDto>[] = [
    { field: "triggerLabTestId", headerName: "When test", width: 200, valueGetter: (_v, row) => testName(row.triggerLabTestId) },
    { field: "triggerFlag", headerName: "Flag", width: 140, renderCell: p => <Chip size="small" label={p.row.triggerFlag} color={p.row.triggerFlag.startsWith("Critical") ? "error" : "warning"} variant="outlined" /> },
    { field: "reflexLabTestId", headerName: "Add test", flex: 1, minWidth: 200, valueGetter: (_v, row) => testName(row.reflexLabTestId) },
    { field: "isActive", headerName: "Status", width: 110, renderCell: p => <Chip size="small" label={p.row.isActive ? "Active" : "Inactive"} color={p.row.isActive ? "success" : "default"} variant="outlined" /> },
  ];

  return (
    <Box>
      <PageHeader breadcrumbs={["Diagnostics"]} title="Reflex rules" />
      {canManage && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center">
            <TextField select label="When test" size="small" value={trigger} onChange={e => setTrigger(e.target.value)} sx={{ minWidth: 220 }}>
              {(tests.data ?? []).map(t => <MenuItem key={t.id} value={t.id}>{t.code} — {t.name}</MenuItem>)}
            </TextField>
            <TextField select label="is flagged" size="small" value={flag} onChange={e => setFlag(e.target.value as DxResultFlag)} sx={{ width: 150 }}>
              {FLAGS.map(f => <MenuItem key={f} value={f}>{f}</MenuItem>)}
            </TextField>
            <TextField select label="then add test" size="small" value={reflex} onChange={e => setReflex(e.target.value)} sx={{ minWidth: 220, flexGrow: 1 }}>
              {(tests.data ?? []).filter(t => t.id !== trigger).map(t => <MenuItem key={t.id} value={t.id}>{t.code} — {t.name}</MenuItem>)}
            </TextField>
            <Button variant="contained" startIcon={create.isPending ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
              disabled={!trigger || !reflex || create.isPending} onClick={() => create.mutate()}>Add</Button>
          </Stack>
          {create.isError && <Alert severity="error" sx={{ mt: 2 }}>{errorMessage(create.error)}</Alert>}
        </Paper>
      )}
      <Paper sx={{ p: 2 }}>
        {rules.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(rules.error)}</Alert>}
        <Box sx={{ height: 480 }}>
          <DataGrid rows={rules.data ?? []} columns={columns} loading={rules.isLoading} getRowId={r => r.id}
            disableRowSelectionOnClick pageSizeOptions={[25, 50]} initialState={{ pagination: { paginationModel: { pageSize: 25 } } }} />
        </Box>
      </Paper>
    </Box>
  );
}
